import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { LuArrowRight, LuCamera, LuSearch, LuX, LuAlertCircle } from 'react-icons/lu';
import { Community } from '../../app-schemas/community/community.interface';
import { UserInterface } from '../../app-schemas/profile.schema';
import { getUser, getFirstLetters } from '../../app-utils';
import CommunityCard from './home-components/CommunityCard';
import WelcomeMessage from './home-components/WelcomeMessage';
import { Link } from 'react-router-dom';
import { SITELINK } from '../../app-routes/Links';
import { useRecommendedCommunities } from '../../api-services/community/useRecommendedCommunities';
import community_api from '../../api-services/community/community.services';
import PostCard from './home-components/PostCard';
import { Loader } from '../../assets/custom-icons';
import { useInView } from 'react-intersection-observer';
import { useInfiniteQuery } from '@tanstack/react-query';
import { FeedsQueryKeys } from '../../api-services/feeds/feeds.keys';
import { fetchFeeds } from '../../api-services/feeds/useFetchNewFeeds';
import { PostInterface } from '../../app-schemas/posts/post.schema';
import profile_api from '../../api-services/profile/profile.services';
import PullOutNav from '../../layouts/~includes/PullOutNav';
import { useTheme } from '../../context/theme-context';
import { PageSkeleton, SkeletonFeed, SkeletonCard } from '../../components/skeletons';

// data type
type Location = {
  latitude: number;
  longitude: number;
};

// custom js fn to handle type Guard
function isLocation(value: any): value is Location {
  return value && typeof value.latitude === 'number' && typeof value.longitude === 'number';
}

// Location Error Modal Component
const LocationErrorModal = ({ 
  isOpen, 
  onClose, 
  onRetry, 
  errorType,
  onGoToSettings
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onRetry: () => void; 
  errorType: 'denied' | 'unavailable' | 'timeout' | 'error';
  onGoToSettings: () => void;
}) => {
  const { isDarkMode } = useTheme();
  
  if (!isOpen) return null;

  const getErrorDetails = () => {
    switch (errorType) {
      case 'denied':
        return {
          title: 'Location Access Denied',
          message: 'You have denied location access. To get personalized community recommendations, please enable location access.',
          suggestion: 'Go to Settings > Privacy > Location Services to enable location access for this site, then click retry.'
        };
      case 'unavailable':
        return {
          title: 'Location Unavailable',
          message: 'We couldn\'t detect your location. This might be due to poor GPS signal or network issues.',
          suggestion: 'Make sure you have a stable internet connection and try again.'
        };
      case 'timeout':
        return {
          title: 'Location Request Timeout',
          message: 'Location request took too long to complete.',
          suggestion: 'Please check your internet connection and try again.'
        };
      default:
        return {
          title: 'Location Error',
          message: 'An unexpected error occurred while trying to get your location.',
          suggestion: 'Please try again or continue without location-based recommendations.'
        };
    }
  };

  const { title, message, suggestion } = getErrorDetails();
  const isPermissionDenied = errorType === 'denied';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`relative rounded-lg shadow-xl max-w-md w-full transform transition-all ${
        isDarkMode ? 'bg-neutral-800' : 'bg-white'
      }`}>
        <div className="p-6">
          {/* Close button */}
          <button
            onClick={onClose}
            className={`absolute top-4 right-4 p-1 rounded-full transition-colors ${
              isDarkMode 
                ? 'hover:bg-neutral-700 text-gray-400 hover:text-white' 
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}
          >
            <LuX size={20} />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <LuAlertCircle className="text-red-600 dark:text-red-400" size={32} />
            </div>
          </div>

          {/* Title */}
          <h3 className={`text-lg font-semibold text-center mb-2 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {title}
          </h3>

          {/* Message */}
          <p className={`text-sm text-center mb-3 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {message}
          </p>

          {/* Suggestion */}
          <p className={`text-xs text-center mb-6 p-3 rounded-lg ${
            isDarkMode 
              ? 'bg-neutral-700 text-gray-300' 
              : 'bg-gray-50 text-gray-600'
          }`}>
            💡 {suggestion}
          </p>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                isDarkMode
                  ? 'bg-neutral-700 text-gray-300 hover:bg-neutral-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Skip for now
            </button>
            {isPermissionDenied ? (
              <button
                onClick={onGoToSettings}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go to Settings
              </button>
            ) : (
              <button
                onClick={onRetry}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const { ref, inView } = useInView();
  const [isWelcomeMssg, setIsWelcomeMssg] = useState(true);
  const [isNav, setNav] = useState(false);
  const [user, setUser] = useState<UserInterface | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [showLocationError, setShowLocationError] = useState(false);
  const [locationErrorType, setLocationErrorType] = useState<'denied' | 'unavailable' | 'timeout' | 'error'>('error');
  const [isRetryingLocation, setIsRetryingLocation] = useState(false);
  const [hasAttemptedLocation, setHasAttemptedLocation] = useState(false);
  const recommendedCommunities = useRecommendedCommunities();
  const { isDarkMode } = useTheme();
  const locationAttemptRef = useRef(false);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: FeedsQueryKeys.all,
    queryFn: fetchFeeds,
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages ? lastPage.currentPage + 1 : undefined,
  });

  const content = useMemo(() =>
      data?.pages.flatMap((page) =>
        page.data.map((post: PostInterface, index: number) => (
          <PostCard
            key={post.post_id}
            index={post.post_id}
            post={post}
            innerRef={page.data.length - 2 === index + 1 ? ref : undefined}
          />
        )),
      ),
    [data, ref],
  );

  useEffect(() => {
    if (inView && hasNextPage) fetchNextPage();
  }, [inView, hasNextPage, fetchNextPage]);

  useEffect(() => {
    // Load user data safely on mount
    const loadUser = () => {
      const userData = getUser();
      setUser(userData);
      setIsLoadingUser(false);
    };
    loadUser();
  }, []);

  // code to get user current location with error handling
  const getUserLocation = useCallback(async (): Promise<Location | null> => {
    return new Promise((resolve, reject) => {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        const error = 'Geolocation is not supported by this browser.';
        console.log(error);
        setLocationErrorType('unavailable');
        setShowLocationError(true);
        setHasAttemptedLocation(true);
        reject(new Error(error));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(position.coords);
        },
        (error) => {
          console.log(`Error getting location:`, error);
          
          // Handle different error types
          switch(error.code) {
            case error.PERMISSION_DENIED:
              setLocationErrorType('denied');
              break;
            case error.POSITION_UNAVAILABLE:
              setLocationErrorType('unavailable');
              break;
            case error.TIMEOUT:
              setLocationErrorType('timeout');
              break;
            default:
              setLocationErrorType('error');
          }
          
          setShowLocationError(true);
          setHasAttemptedLocation(true);
          reject(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    });
  }, []);

  const setLocation = useCallback(async (data: any) => {
    try {
      const res = await profile_api.UpdateLocation(data);
      console.log('Location updated successfully:', res);
      return res;
    } catch (error) {
      console.log('Error updating location:', error);
      throw error;
    } finally {
      console.log(false);
    }
  }, []);

  const handleLocationCommunities = useCallback(async () => {
    try {
      const position = await getUserLocation();

      if (isLocation(position)) {
        const coordinates: Location = position;
        let payload = {
          longitude: coordinates.longitude,
          latitude: coordinates.latitude,
        };
        await setLocation(payload);
        setShowLocationError(false); // Clear error if successful
        setHasAttemptedLocation(true);
      }
    } catch (error) {
      console.log('Location handling failed:', error);
      // Error is already handled in getUserLocation
    }
  }, [getUserLocation, setLocation]);

  const handleRetryLocation = useCallback(async () => {
    setIsRetryingLocation(true);
    setShowLocationError(false);
    setHasAttemptedLocation(false);
    locationAttemptRef.current = false;
    
    try {
      await handleLocationCommunities();
    } catch (error) {
      console.log('Retry failed:', error);
    } finally {
      setIsRetryingLocation(false);
    }
  }, [handleLocationCommunities]);

  const handleGoToSettings = useCallback(() => {
    // Close the modal
    setShowLocationError(false);
    
    // Provide instructions for common browsers
    const userAgent = navigator.userAgent.toLowerCase();
    let settingsUrl = '';
    
    if (userAgent.includes('chrome')) {
      settingsUrl = 'chrome://settings/content/location';
    } else if (userAgent.includes('firefox')) {
      settingsUrl = 'about:preferences#privacy';
    } else if (userAgent.includes('safari')) {
      settingsUrl = 'https://support.apple.com/guide/safari/manage-website-access-to-location-services-ibrw7e9c3a58/mac';
    }
    
    if (settingsUrl) {
      window.open(settingsUrl, '_blank');
    } else {
      // Fallback: Show alert with instructions
      alert('Please go to your browser settings, find Privacy/Location settings, and enable location access for this site, then click "Retry" in the app.');
    }
  }, []);

  const handleJoinCommunity = useCallback(async (id: number) => {
    try {
      const res = await community_api.JoinCommunity(id);
      if (res?.data?.status) {
        recommendedCommunities.refetch();
      }
    } catch (error) {
      console.error('Error joining community:', error);
    }
  }, [recommendedCommunities]);

  // Add a method to reset location attempt (can be called from settings)
  const resetLocationAttempt = useCallback(() => {
    setHasAttemptedLocation(false);
    locationAttemptRef.current = false;
    setShowLocationError(false);
    handleLocationCommunities();
  }, [handleLocationCommunities]);

  // Expose reset method to window for settings page to call if needed
  useEffect(() => {
    (window as any).resetLocationAttempt = resetLocationAttempt;
    return () => {
      delete (window as any).resetLocationAttempt;
    };
  }, [resetLocationAttempt]);

  useEffect(() => {
    // Only attempt location once per session if user exists and hasn't attempted yet
    if (user && !hasAttemptedLocation && !locationAttemptRef.current && !showLocationError && !isRetryingLocation) {
      locationAttemptRef.current = true;
      handleLocationCommunities();
    }
  }, [user, hasAttemptedLocation, showLocationError, isRetryingLocation, handleLocationCommunities]);

  const renderProfilePicture = () => {
    if (!user) return null;
    
    return user.profile_picture_url ? (
      <img
        alt="profile"
        src={user.profile_picture_url}
        className="object-cover rounded-full w-[60px] h-[42px] bg-gray-200 shadow-sm"
      />
    ) : (
      <div className="w-[45px] h-[45px] bg-[#223f64] rounded-full border border-green-100 flex items-center justify-center">
        <h3 className="text-white text-[1.3rem] uppercase font-[600]">
          {getFirstLetters(user.first_name ?? user.username)}
        </h3>
      </div>
    );
  };

  const openNav = () => setNav(true);
  const closeNav = () => setNav(false);

  // Show skeleton loading states
  if (isLoadingUser) {
    return <PageSkeleton type="home" />;
  }

  // Handle case where user is not logged in
  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen bg-white dark:bg-neutral-800">
        <div className="text-center">
          <p className="text-gray-600 dark:text-white mb-4">Please log in to continue</p>
          <Link to="/login" className="text-blue-600 hover:underline">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <main>
      {/* Location Error Modal */}
      <LocationErrorModal
        isOpen={showLocationError}
        onClose={() => setShowLocationError(false)}
        onRetry={handleRetryLocation}
        onGoToSettings={handleGoToSettings}
        errorType={locationErrorType}
      />

      {/* make a new post section */}
      <section className="items-center bg-white dark:bg-neutral-800 px-2 py-2 flex gap-2 item-center justify-betweeen">
        <button type="button" className="px-2" onClick={openNav}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 20L3 20V18L21 18V20ZM15 13L3 13V11L15 11L15 13ZM21 6L3 6V4L21 4V6Z"
              fill={isDarkMode ? "white": "black"}
              fillOpacity={isDarkMode ? "1": "0.5"}
            />
          </svg>
        </button>

        <Link
          to={SITELINK.COMPOSER}
          className="flex justify-center items-center gap-2 w-full madimi-one text-sm p-1 px-4 border-[#E7ECF0] text-start text-gray-600 font-[400] border rounded-[50px] bg-[#E7ECF0] dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-300"
        >
          <span className="text-[16px] tracking-[-1px]">Have something to say?</span>
          <LuCamera className="text-[24px]" />
        </Link>

        <Link to={`${SITELINK.MY_PROFILE}/${user?.username}`} className="w-[70px] block pr-2">
          {renderProfilePicture()}
        </Link>
      </section>

      {/* welcome alert area */}
      <section className="bg-white dark:bg-neutral-800 pt-2">
        {/* ! new account Welcome message */}
        {user?.residency_city && isWelcomeMssg && (
          <WelcomeMessage user={user} onClose={() => setIsWelcomeMssg(false)} />
        )}

        <div className="font-[500] flex justify-between text-gray-700 pt-3 px-5 items-center">
          <span className="text-[16px] font-bold mt-1 text-[#000] dark:text-white">Find communities</span>

          <Link
            to={SITELINK.APP_SEARCH}
            className="text-[#1976D2] text-[16px] gap-2 flex justify-between items-center border border-[#1976D2] px-3 py-0.5 rounded-full dark:border-blue-400 dark:text-blue-400"
          >
            <LuSearch /> <span>Search</span>
          </Link>
        </div>

        {/*  recommended community list/action btn - Show skeletons while loading */}
        {recommendedCommunities.isLoading ? (
          <div className="search-item-filter w-full flex overflow-x-scroll scroll-smooth scroll-style mt-4 pb-5">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="max-w-[30vw] ml-2 w-full">
                <SkeletonCard variant="community" />
              </div>
            ))}
            {/* Find More skeleton */}
            <div className="bg-white dark:bg-neutral-800 flex custom-min-width justify-center items-center p-6 px-3">
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                <div className="w-20 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
            </div>
          </div>
        ) : !!recommendedCommunities?.data?.length && (
          <div className="search-item-filter w-full flex overflow-x-scroll scroll-smooth scroll-style mt-4 pb-5">
            {recommendedCommunities?.data?.map((community: Community, index: number) => (
              <CommunityCard
                key={index}
                community={community}
                onClick={handleJoinCommunity}
                title="join"
                className="max-w-[30vw] ml-2 w-full"
              />
            ))}

            <Link
              to={SITELINK.MORE_COMMUNITIES}
              className="bg-[#fff] dark:bg-neutral-800 flex custom-min-width justify-center items-center p-6 px-3"
            >
              <div className="flex flex-col items-center gap-2 text-center">
                <button className="justify-center flex flex-col item-center mt-1 mx-5">
                  <LuArrowRight className="text-[3rem] bg-custom-600 text-white font-bold py-2 rounded-full shadow-xl" />
                </button>

                <p className="justify-center text-sm font-semibold mt-2 text-gray-900 dark:text-white">Find More</p>
              </div>
            </Link>
          </div>
        )}
      </section>

      {/* community feeds section - Show skeletons while loading */}
      <section className="mb-20 bg-white dark:bg-neutral-800">
        {isLoading ? (
          <SkeletonFeed count={3} variant="post" />
        ) : (
          <>
            <div className="text-sm">{content}</div>

            {isFetchingNextPage && (
              <div className="flex py-5 justify-center">
                <img src={Loader} alt="loader" className="h-[20px] w-[20px]" />
              </div>
            )}

            {!hasNextPage && (
              <div className="text-xs flex justify-center bg-white dark:bg-neutral-800 py-5 text-gray-600 dark:text-white">
                No more content
              </div>
            )}
          </>
        )}
      </section>

      <PullOutNav action={isNav} onClose={closeNav} />
    </main>
  );
};

export default Home;